import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { TenantUserRolePermissionsService } from 'src/app/core/services/application/tenant-user-role-permissions.service';
import { UserPermission } from '../models/user-permission';
import { TenantContextService } from '../tenant-context/tenant-context.service';
import { Subject, takeUntil } from 'rxjs';

@Directive({
    selector: '[cdpHasUserPermissions]',
    standalone: false
})
export class HasUserPermissionsDirective implements AfterViewInit, OnDestroy {
    /**
     * Checks a user's config permissions and disables or hides an element if there is insufficient access
     */
    @Input('cdpHasUserPermissions') public requiredPermissions: Array<string> = [];

    /**
     * The action to take for the element if permissions are denied: disable or hide (default).
     *
     * Some elements, such as a hc-navbar-link, do not support 'disabled' and need to be hidden instead
     */
    @Input() public permissionDeniedAction: 'disable' | 'hide' = 'hide';

    private destroy$ = new Subject<boolean>();

    public constructor(
        private element: ElementRef,
        private renderer: Renderer2,
        private tenantContextService: TenantContextService,
        private tenantPermissionsService: TenantUserRolePermissionsService
    ) {}

    public ngAfterViewInit(): void {
        const currentTenantId: string | undefined = this.tenantContextService.currentTenant?.tenantId;

        this.tenantPermissionsService
            .getAllPermissions()
            .pipe(takeUntil(this.destroy$))
            .subscribe((userPermissions: Array<UserPermission> | null) => {
                const missingPermissions: Array<string> = this.requiredPermissions.filter(
                    (requiredPermission: string) => {
                        const hasRequiredPermission =
                            userPermissions
                                ?.find((userPermission: UserPermission) => userPermission.tenantId === currentTenantId)
                                ?.permissions.includes(requiredPermission) ?? false;

                        return !hasRequiredPermission;
                    }
                );

                const hasPermission: boolean = missingPermissions.length == 0;

                if (!hasPermission) {
                    if (this.permissionDeniedAction == 'disable') {
                        this.renderer.setProperty(this.element.nativeElement, 'disabled', true);
                        this.renderer.setProperty(
                            this.element.nativeElement,
                            'title',
                            'Insufficient permissions. Please contact an administrator.'
                        );
                    } else if (this.permissionDeniedAction == 'hide') {
                        this.renderer.setStyle(this.element.nativeElement, 'display', 'none');
                    }

                    console.group('User is missing the following permissions:');
                    missingPermissions.forEach((permission: string) => console.log(permission));
                    console.groupEnd();
                }
            });
    }
    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
